import { useState } from 'react';
import {
  regionOptions,
  yearOptions,
  sortOptions,
  defaultFilter,
  type FilterState,
  type SortKey,
} from '@/utils/movieUtils';

interface FilterBarProps {
  filter: FilterState;
  onChange: (filter: FilterState) => void;
}

interface GroupProps {
  label: string;
  options: string[];
  value: string;
  onSelect: (value: string) => void;
}

function ChipGroup({ label, options, value, onSelect }: GroupProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
      <span className="shrink-0 text-sm text-foreground-500 sm:w-12">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onSelect(opt)}
            className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors ${
              value === opt
                ? 'bg-primary-500 text-background-950 font-semibold'
                : 'bg-background-200/70 text-foreground-700 hover:bg-background-300'
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FilterBar({ filter, onChange }: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const update = (patch: Partial<FilterState>) => onChange({ ...filter, ...patch });

  const body = (
    <div className="space-y-4">
      <ChipGroup label="地区" options={regionOptions} value={filter.region} onSelect={(v) => update({ region: v })} />
      <div className="h-px bg-background-200/70" />
      <ChipGroup label="年份" options={yearOptions} value={filter.year} onSelect={(v) => update({ year: v })} />
      <div className="h-px bg-background-200/70" />
      <ChipGroup
        label="排序"
        options={sortOptions.map((s) => s.label)}
        value={sortOptions.find((s) => s.value === filter.sort)?.label ?? '最新'}
        onSelect={(v) => {
          const found = sortOptions.find((s) => s.label === v);
          if (found) update({ sort: found.value as SortKey });
        }}
      />
    </div>
  );

  return (
    <div className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
      {/* Desktop */}
      <div className="hidden md:block">
        {body}
        <div className="mt-4 pt-4 border-t border-background-200/70">
          <button
            type="button"
            onClick={() => onChange({ ...defaultFilter })}
            className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>
            重置筛选
          </button>
        </div>
      </div>

      {/* Mobile */}
      <div className="md:hidden">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-2 text-sm font-semibold text-foreground-900 cursor-pointer"
        >
          <span className="flex items-center gap-2">
            <i className="ri-filter-3-line text-primary-400"></i>
            筛选条件
          </span>
          <i className={open ? 'ri-arrow-up-s-line text-xl' : 'ri-arrow-down-s-line text-xl'}></i>
        </button>
        {open && (
          <div className="mt-4">
            {body}
            <button
              type="button"
              onClick={() => {
                onChange({ ...defaultFilter });
                setOpen(false);
              }}
              className="mt-4 w-full h-11 rounded-md border border-background-300 text-sm font-semibold text-foreground-900 cursor-pointer"
            >
              重置筛选
            </button>
          </div>
        )}
      </div>
    </div>
  );
}